import uiI18n from 'i18next';
import { Component } from 'react';
import type { CSSProperties, ErrorInfo, ReactNode } from 'react';
import { claimDynamicImportReload } from '@/platform/lib/startupErrorHandling';
import { recordUiError } from '@/platform/replay/errorMarkers';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  resetKeys?: Array<string | number>;
  resetOnPropsChange?: boolean;
  isolate?: boolean;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  retryCount: number;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
    retryCount: 0,
  };

  private resetTimeoutId: NodeJS.Timeout | null = null;
  private previousResetKeys: Array<string | number> = [];

  public static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    recordUiError(this.props.isolate ? 'tab' : 'app');

    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    this.setState({
      error,
      errorInfo,
    });

    if (claimDynamicImportReload(error)) {
      window.location.reload();
      return;
    }

    if (this.shouldAutoRetry(error) && this.state.retryCount < 3) {
      const delay = Math.min(1000 * Math.pow(2, this.state.retryCount), 5000);
      console.warn(`Auto-retrying in ${delay}ms (attempt ${this.state.retryCount + 1}/3)`);

      this.resetTimeoutId = setTimeout(() => {
        this.resetErrorBoundary();
      }, delay);
    }
  }

  public componentDidUpdate(prevProps: Props) {
    const { resetKeys, resetOnPropsChange } = this.props;
    const { hasError } = this.state;

    if (hasError && prevProps.resetKeys !== resetKeys) {
      if (resetKeys?.some((key, idx) => key !== this.previousResetKeys[idx])) {
        this.resetErrorBoundary();
      }
    }

    if (hasError && resetOnPropsChange && prevProps.children !== this.props.children) {
      this.resetErrorBoundary();
    }

    this.previousResetKeys = resetKeys || [];
  }

  public componentWillUnmount() {
    if (this.resetTimeoutId) {
      clearTimeout(this.resetTimeoutId);
    }
  }

  private shouldAutoRetry(error: Error): boolean {
    const retryableErrors = [
      'NetworkError',
      'ChunkLoadError',
      'Loading chunk',
      'Failed to fetch',
      'Network request failed',
      'Socket connection error',
      'WebSocket',
      '502',
      'Bad Gateway',
      '503',
      'Service Unavailable',
      '504',
      'Gateway Timeout',
    ];

    return retryableErrors.some(msg => error.message?.includes(msg) || error.stack?.includes(msg));
  }

  private getErrorContext(error: Error): { title: string; suggestions: string[] } | null {
    const message = error.message || '';
    const _stack = error.stack || '';

    // Bad Gateway / Backend unavailable
    if (
      message.includes('502') ||
      message.includes('Bad Gateway') ||
      message.includes('ECONNREFUSED')
    ) {
      return {
        title: uiI18n.t('platform.ui.backendServerUnavailable'),
        suggestions: [
          'The backend service may have crashed or is not running',
          'Check if backend is running: curl http://localhost:7051/api/health',
          'Restart backend: pnpm services:restart:backend',
          'Or restart all services: pnpm restart:dev',
          'Check backend logs: pnpm logs:backend',
        ],
      };
    }

    // Service Unavailable / Database issues
    if (
      message.includes('503') ||
      message.includes('Service Unavailable') ||
      message.includes('database')
    ) {
      return {
        title: uiI18n.t('platform.ui.serviceTemporarilyUnavailable'),
        suggestions: [
          'The backend may be experiencing database connection issues',
          'Check database status: pnpm db:health:dev',
          'Check service health: curl http://localhost:7051/api/health',
          'Restart services if needed: pnpm restart:dev',
        ],
      };
    }

    // Timeout errors
    if (
      message.includes('504') ||
      message.includes('Gateway Timeout') ||
      message.includes('timeout')
    ) {
      return {
        title: uiI18n.t('platform.ui.requestTimedOut'),
        suggestions: [
          'The server took too long to respond',
          'This might indicate performance issues or heavy load',
          'Try again in a moment',
          'If this persists, check backend logs: pnpm logs:backend',
        ],
      };
    }

    // Schema/Migration errors
    if (
      message.includes('schema') ||
      message.includes('migration') ||
      message.includes('table') ||
      message.includes('column')
    ) {
      return {
        title: uiI18n.t('platform.ui.databaseSchemaIssue'),
        suggestions: [
          'There may be a mismatch between code and database schema',
          'Run schema migration: cd backend && pnpm db:push:auto',
          'Check schema status: cd backend && pnpm db:status',
          'Check health endpoint: curl http://localhost:7051/api/health',
        ],
      };
    }

    return null;
  }

  private resetErrorBoundary = () => {
    if (this.resetTimeoutId) {
      clearTimeout(this.resetTimeoutId);
      this.resetTimeoutId = null;
    }

    this.setState(prevState => ({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: prevState.retryCount + 1,
    }));
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return <>{this.props.fallback}</>;
      }

      const isRetryable = this.shouldAutoRetry(this.state.error!);
      const isRetrying = isRetryable && this.state.retryCount < 3;
      const errorContext = this.state.error ? this.getErrorContext(this.state.error) : null;

      // When `isolate` is set the boundary guards a sub-tree (e.g. a single tab
      // view) rather than the whole app, so the fallback must stay *contained*
      // within its parent. A fixed, full-viewport overlay would otherwise cover
      // unrelated UI — and since inactive tabs stay mounted-but-hidden, a crash
      // in a background tab would paint over the active one.
      // All colors below are intentionally inline (not Tailwind classes): the
      // crash screen must render even when the CSS pipeline failed to load.
      // Values follow the iv-dark / iv-danger / iv-warning families.
      const containerStyle: CSSProperties = this.props.isolate
        ? {
            position: 'relative',
            height: '100%',
            width: '100%',
            padding: '20px',
            backgroundColor: '#1a1721',
            color: '#f4f3f6',
            fontFamily: 'monospace',
            overflow: 'auto',
            WebkitOverflowScrolling: 'touch',
          }
        : {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 99999,
            padding: '20px',
            margin: 0,
            backgroundColor: '#1a1721',
            color: '#f4f3f6',
            fontFamily: 'monospace',
            overflow: 'auto',
            WebkitOverflowScrolling: 'touch',
          };

      return (
        <div style={containerStyle}>
          <h2
            style={{
              color: '#ef4444',
              fontSize: '24px',
              fontWeight: 'bold',
              marginBottom: '16px',
            }}
          >
            {errorContext?.title ||
              (this.props.isolate
                ? uiI18n.t('platform.ui.componentError')
                : uiI18n.t('platform.ui.applicationError'))}
          </h2>

          {errorContext && (
            <div
              style={{
                padding: '16px',
                backgroundColor: '#78350f',
                border: '1px solid #b45309',
                borderRadius: '6px',
                marginTop: '16px',
              }}
            >
              <h3
                style={{
                  color: '#fef3c7',
                  fontWeight: '600',
                  marginBottom: '12px',
                  fontSize: '16px',
                }}
              >
                {uiI18n.t('platform.ui.suggestedActions')}
              </h3>
              <ul
                style={{
                  listStyleType: 'disc',
                  paddingLeft: '20px',
                }}
              >
                {errorContext.suggestions.map((suggestion, index) => (
                  <li
                    key={index}
                    style={{
                      color: '#fef3c7',
                      marginBottom: '8px',
                      lineHeight: '1.5',
                    }}
                  >
                    {suggestion}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {isRetrying && (
            <div
              style={{
                padding: '12px',
                backgroundColor: '#2a2535',
                borderRadius: '4px',
                marginTop: '12px',
              }}
            >
              <p>
                {uiI18n.t('platform.ui.autoRetryingAttempt')} {this.state.retryCount + 1}/3)
              </p>
            </div>
          )}

          {this.state.retryCount > 0 && (
            <p style={{ color: '#f0b357', marginTop: '12px' }}>
              {uiI18n.t('platform.ui.retryAttempts')} {this.state.retryCount}
            </p>
          )}

          <details
            style={{
              whiteSpace: 'pre-wrap',
              marginTop: '16px',
              fontSize: '14px',
            }}
          >
            <summary
              style={{
                cursor: 'pointer',
                marginBottom: '12px',
                color: '#b9a3f5',
                userSelect: 'none',
              }}
            >
              {uiI18n.t('platform.ui.errorDetailsClickToExpand')}
            </summary>
            <div
              style={{
                backgroundColor: '#100d16',
                padding: '12px',
                borderRadius: '4px',
              }}
            >
              <strong style={{ color: '#ee9a9c' }}>{uiI18n.t('platform.ui.error')}</strong>
              <pre
                className="overflow-auto mt-2 text-sm"
                style={{
                  overflow: 'auto',
                  marginTop: '8px',
                  fontSize: '13px',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {this.state.error && this.state.error.toString()}
              </pre>
              <strong
                style={{
                  color: '#ee9a9c',
                  display: 'block',
                  marginTop: '16px',
                }}
              >
                {uiI18n.t('platform.ui.stack')}
              </strong>
              <pre
                className="overflow-auto mt-2 text-xs"
                style={{
                  overflow: 'auto',
                  marginTop: '8px',
                  fontSize: '11px',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {this.state.error?.stack}
              </pre>
              {this.state.errorInfo && (
                <>
                  <strong
                    style={{
                      color: '#ee9a9c',
                      display: 'block',
                      marginTop: '16px',
                    }}
                  >
                    {uiI18n.t('platform.ui.componentStack')}
                  </strong>
                  <pre
                    className="overflow-auto mt-2 text-xs"
                    style={{
                      overflow: 'auto',
                      marginTop: '8px',
                      fontSize: '11px',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {this.state.errorInfo.componentStack}
                  </pre>
                </>
              )}
            </div>
          </details>

          <div
            style={{
              marginTop: '20px',
              display: 'flex',
              gap: '12px',
              flexWrap: 'wrap',
            }}
          >
            <button
              onClick={this.resetErrorBoundary}
              style={{
                padding: '8px 20px',
                backgroundColor: '#14a368',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '500',
              }}
            >
              {uiI18n.t('platform.ui.tryAgain')}
            </button>

            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '8px 20px',
                backgroundColor: '#4b16c9',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '500',
              }}
            >
              {uiI18n.t('platform.ui.reloadApplication')}
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
